import { useMemo } from 'react';
import { getFormFieldGroups } from '../../../document/pages/Assistance/useDocumentConfig';
import { IChapter, IPosition } from '../pages/Assistance';

const ARTICLE_FIELD_PREFIX = 'positions__article_sets__articles';

/**
 * Groups articles by position for rendering in ItemDataSection
 */
export const groupArticlesByPosition = (allRelevantArticleSets: any[], articleFieldConfigs: any[]) => {
    if (!articleFieldConfigs?.length) return [];

    const grouped = new Map();
    let virtualItemIndex = 0;

    allRelevantArticleSets.forEach(({ position, set, setIndex }) => {
        const positionKey = `${position.positionIndex}`;

        if (!grouped.has(positionKey)) {
            grouped.set(positionKey, {
                positionIndex: position.positionIndex,
                positionNumber: position.number?.value || '',
                positionText: position.text?.value || '',
                pageIndex: position.pageIndex,
                position,
                articles: [],
                articleSets: [],
            });
        }

        const positionGroup = grouped.get(positionKey);
        const setStartIndex = positionGroup.articles.length;

        (set?.articles || []).forEach((article, articleIndex) => {
            const fields = getFormFieldGroups(
                article,
                articleFieldConfigs,
                ARTICLE_FIELD_PREFIX,
                virtualItemIndex
            ).flat();

            const indexInChapter = position.indexInChapter ?? null;

            const updatePayload = {
                itemIndex: position.positionIndex,
                setIndex,
                articleIndex,
                indexInChapter,
            };

            const enrichedFields = fields.map((field) => ({
                ...field,
                itemIndex: virtualItemIndex,
                articleIndex,
                updatePayload,
                data: {
                    ...field.data,
                    updatePayload,
                },
            }));

            positionGroup.articles.push({
                article: article,
                fields: enrichedFields,
                setIndex,
                articleIndex,
                suggestions: article.suggestions || [],
            });

            virtualItemIndex += 1;
        });

        positionGroup.articleSets.push({
            setIndex,
            startIndex: setStartIndex,
            endIndex: positionGroup.articles.length - 1,
            suggestions: set?.articles?.[0]?.suggestions || [],
            isComplete: set?.isComplete || false,
        });
    });

    return Array.from(grouped.values());
};

/**
 * Hook to process and filter positions data
 */
export const usePositionsData = (chapterMap: Map<string, IChapter>, chapter: string, articleFieldConfigs: any[]) => {
    // Extract all relevant positions from the expanded chapter
    const relevantPositions = useMemo(() => {
        if (!chapterMap) return [];

        const collected: IPosition[] = [];

        for (const [chapterNumber, chapterData] of chapterMap) {
            if (!chapterData.positions) continue;

            for (const pos of chapterData.positions) {
                if (pos.prediction !== 'PreText' && pos.isRelevant?.value === 'True') {
                    collected.push(pos);
                }
            }
        }

        return collected;
    }, [chapterMap, chapter]);

    // Collect all articleSets from all relevant positions
    const allRelevantArticleSets = useMemo(() => {
        return relevantPositions.flatMap((position) => {
            const sets = position.articleSets ?? [];
            return sets.map((set, setIndex) => ({
                position,
                set,
                setIndex,
            }));
        });
    }, [relevantPositions]);

    // Group articles by position for ItemDataSection
    const articlesByPosition = useMemo(() => {
        return groupArticlesByPosition(allRelevantArticleSets, articleFieldConfigs);
    }, [allRelevantArticleSets, articleFieldConfigs]);

    return {
        relevantPositions,
        allRelevantArticleSets,
        articlesByPosition,
    };
};
